import { useEffect, useId, useState } from 'react';

import { Avatar, Badge, Button, Divider, Icon } from '@ds/core';
import { Alert, Modal } from '@ds/feedback';
import { Timeline } from '@ds/data';
import { FormField, Input, Select, Switch, Textarea } from '@ds/forms';
import { Tabs } from '@ds/navigation';
import { dataCurta, dataEHoraPorExtenso, diaIso, horaDe } from '@/comuns/formato';
import { useAviso } from '@/comuns/toast';
import { useConfirmacao } from '@/comuns/useConfirmacao';
import { dados } from '@/data/porta';
import { atual } from '@/session/sessao';
import type {
    Agendamento, EdicaoDeAgendamento, Lembrete, ProcedimentoParaAgendar, Profissional,
    RegistroDeHistorico, Sala, StatusDoAgendamento, VinculoDoAgendamento,
} from '@/data/tipos';
import { NotificarPaciente } from './NotificarPaciente';

const STATUS_OPCOES: { value: StatusDoAgendamento; label: string }[] = [
    { value: 'confirmado', label: 'Confirmado' },
    { value: 'pendente', label: 'Pendente' },
    { value: 'concluido', label: 'Concluído' },
    { value: 'cancelado', label: 'Cancelado' },
    { value: 'faltou', label: 'Faltou' },
];

const STATUS_TOM: Record<StatusDoAgendamento, 'success' | 'neutral' | 'info' | 'danger' | 'warning'> = {
    confirmado: 'success', pendente: 'neutral', concluido: 'info', cancelado: 'danger', faltou: 'warning', bloqueio: 'neutral',
};

const CANAL_ROTULO: Record<Lembrete['canal'], string> = {
    whatsapp: 'WhatsApp', email: 'E-mail', ambos: 'WhatsApp e e-mail',
};

interface Listas {
    profissionais: Profissional[];
    salas: Sala[];
    procedimentos: ProcedimentoParaAgendar[];
}

/** O formulário guarda ID: é o que o banco grava. O nome é só para ler. */
interface Formulario {
    data: string;
    hora: string;
    profissionalId: string;
    procedimentoId: string;
    salaId: string;
    status: StatusDoAgendamento;
    observacoes: string;
}

/** Campos cuja mudança o paciente precisa saber. Observação interna, não. */
const RELEVANTES: { campo: keyof Formulario; rotulo: string }[] = [
    { campo: 'data', rotulo: 'Data' },
    { campo: 'hora', rotulo: 'Horário' },
    { campo: 'profissionalId', rotulo: 'Profissional' },
    { campo: 'procedimentoId', rotulo: 'Procedimento' },
    { campo: 'salaId', rotulo: 'Sala' },
    { campo: 'status', rotulo: 'Situação' },
];

/** Nome comparável: a agenda escreve "Dra. Ana Prado"; o cadastro, às vezes, "Ana Prado". */
function chave(nome: string): string {
    return nome.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/^dra?\.?\s+/, '').trim();
}

function idPeloNome(lista: { id: string; nome: string }[], nome: string | undefined): string {
    if (!nome) return '';
    return lista.find((item) => chave(item.nome) === chave(nome))?.id ?? '';
}

function formularioDe(a: Agendamento, listas: Listas): Formulario {
    return {
        data: diaIso(new Date(a.inicioEm)),
        hora: horaDe(a.inicioEm),
        profissionalId: idPeloNome(listas.profissionais, a.profissional),
        procedimentoId: idPeloNome(listas.procedimentos, a.procedimento),
        salaId: idPeloNome(listas.salas, a.sala),
        status: a.status,
        observacoes: a.observacoes ?? '',
    };
}

/** O valor como a pessoa lê. Id que o cadastro não conhece cai no nome que o agendamento já tinha. */
function exibir(campo: keyof Formulario, form: Formulario, listas: Listas, a: Agendamento): string {
    if (campo === 'profissionalId') return listas.profissionais.find((p) => p.id === form.profissionalId)?.nome ?? a.profissional;
    if (campo === 'procedimentoId') return listas.procedimentos.find((p) => p.id === form.procedimentoId)?.nome ?? a.procedimento;
    if (campo === 'salaId') return form.salaId ? (listas.salas.find((s) => s.id === form.salaId)?.nome ?? '') : '';
    if (campo === 'status') return STATUS_OPCOES.find((s) => s.value === form.status)?.label ?? form.status;
    return String(form[campo]);
}

/** Só o que ainda vai acontecer se cancela. Bloqueio se tira editando; concluído e falta já passaram. */
const CANCELAVEIS: StatusDoAgendamento[] = ['confirmado', 'pendente'];

/** O texto que se oferece para mandar ao paciente — editável antes de sair. */
function mensagemDeAviso(a: Agendamento): string {
    const primeiroNome = a.paciente.trim().split(/\s+/)[0] ?? a.paciente;
    const dia = diaIso(new Date(a.inicioEm));
    const quando = `${dia.slice(8, 10)}/${dia.slice(5, 7)} às ${horaDe(a.inicioEm)}`;
    const deQue = a.procedimento ? ` de ${a.procedimento.toLowerCase()}` : '';
    if (a.status === 'cancelado') return `Olá, ${primeiroNome}! Seu agendamento${deQue} de ${quando} foi cancelado. Quer que a gente veja outro horário?`;
    return `Olá, ${primeiroNome}! Seu agendamento${deQue} mudou: agora é ${quando}${a.profissional ? `, com ${a.profissional}` : ''}. Qualquer dúvida, é só responder esta mensagem.`;
}

export interface DetalheDoAgendamentoProps {
    agendamento: Agendamento | null;
    /** Enquanto o cadastro não carrega, as listas que a agenda já tem. */
    profissionais: Profissional[];
    salas: Sala[];
    onClose: () => void;
    onSalvo: () => void;
    onAbrirPaciente: (pacienteId: string) => void;
    onAbrirLead: (vinculo: VinculoDoAgendamento) => void;
}

/**
 * Detalhe do agendamento: informação, edição, lembretes e histórico.
 *
 * A alteração acontece aqui, registra autor e mudança no histórico e oferece
 * avisar o paciente — mas **não avisa sozinha**: numa reorganização de agenda,
 * quem comunica é uma pessoa, escolhendo o canal.
 */
export function DetalheDoAgendamento({
    agendamento, profissionais, salas, onClose, onSalvo, onAbrirPaciente, onAbrirLead,
}: DetalheDoAgendamentoProps) {
    const aviso = useAviso();
    const [confirmar, confirmacao] = useConfirmacao();
    const idData = useId();
    const idHora = useId();
    const idProcedimento = useId();
    const idProfissional = useId();
    const idSala = useId();
    const idSituacao = useId();

    const [aba, setAba] = useState('info');
    const [editando, setEditando] = useState(false);
    /* O agendamento como está no servidor AGORA. A agenda guarda o que abriu;
       depois de salvar, o detalhe mostra o que voltou. */
    const [base, setBase] = useState<Agendamento | null>(agendamento);
    const [listas, setListas] = useState<Listas>({ profissionais, salas, procedimentos: [] });
    const [erroDasListas, setErroDasListas] = useState<string | null>(null);
    const [form, setForm] = useState<Formulario | null>(null);
    const [erroDoForm, setErroDoForm] = useState<string | null>(null);
    const [historico, setHistorico] = useState<RegistroDeHistorico[]>([]);
    const [lembretes, setLembretes] = useState<Lembrete[]>([]);
    const [erroDeCarga, setErroDeCarga] = useState<string | null>(null);
    const [notificar, setNotificar] = useState<string | null>(null);
    const [salvando, setSalvando] = useState(false);

    const id = agendamento?.id;

    useEffect(() => {
        let vivo = true;
        dados().opcoesDoAgendamento()
            .then((o) => { if (vivo) setListas({ profissionais: o.profissionais, salas: o.salas, procedimentos: o.procedimentos }); })
            .catch((e: unknown) => { if (vivo) setErroDasListas(e instanceof Error ? e.message : 'Não foi possível ler o cadastro.'); });
        return () => { vivo = false; };
    }, []);

    useEffect(() => {
        setBase(agendamento);
        if (!agendamento) return;
        setEditando(false);
        setAba('info');
        setNotificar(null);
        setErroDoForm(null);
        setErroDeCarga(null);

        let vivo = true;
        Promise.all([
            dados().historicoDoAgendamento(agendamento.id),
            dados().lembretesDoAgendamento(agendamento.id),
        ])
            .then(([h, l]) => {
                /* O modal pode ter fechado (ou trocado de agendamento) enquanto a
                   busca corria: sem esta guarda, o histórico de um cai no outro. */
                if (!vivo) return;
                setHistorico(h);
                setLembretes(l);
            })
            .catch((e: unknown) => {
                /* Vazio sem explicação parecia "sem histórico". */
                if (vivo) setErroDeCarga(e instanceof Error ? e.message : 'Não foi possível ler o histórico e os lembretes.');
            });
        return () => { vivo = false; };
    }, [agendamento, id]);

    useEffect(() => {
        if (base && !editando) setForm(formularioDe(base, listas));
    }, [base, listas, editando]);

    if (!agendamento || !base || !form) return null;

    const original = formularioDe(base, listas);
    const mudados = RELEVANTES.filter((r) => {
        /* Profissional e procedimento vazios são "não escolhido", não "tirado". */
        if ((r.campo === 'profissionalId' || r.campo === 'procedimentoId') && !form[r.campo]) return false;
        return String(form[r.campo]) !== String(original[r.campo]);
    });
    const sujo = mudados.length > 0 || form.observacoes !== original.observacoes;
    const alterar = <C extends keyof Formulario>(campo: C, valor: Formulario[C]) => {
        setErroDoForm(null);
        setForm((f) => (f ? { ...f, [campo]: valor } : f));
    };

    const salvar = async (): Promise<boolean> => {
        if (!sujo) { setEditando(false); return true; }
        if (!form.data || !form.hora) {
            setErroDoForm('Informe a data e o horário.');
            return false;
        }
        setSalvando(true);
        const agora = new Date().toISOString();
        const autor = atual()?.nome ?? 'Usuário';

        const registros: RegistroDeHistorico[] = mudados.map((r) => ({
            id: `${base.id}-${r.campo}-${agora}`,
            titulo: `Alterou ${r.rotulo}`,
            quandoEm: agora,
            autor,
            icone: 'pencil',
            tom: 'warning',
            descricao: `${exibir(r.campo, original, listas, base) || '—'} → ${exibir(r.campo, form, listas, base) || '—'}`,
        }));
        if (registros.length === 0) {
            registros.push({ id: `${base.id}-obs-${agora}`, titulo: 'Alterou observações', quandoEm: agora, autor, icone: 'pencil' });
        }

        /* Só o que mudou vai ao servidor. Mandar tudo regravava campos que a
           pessoa nem tocou — e o nome sem cadastro era ignorado em silêncio. */
        const mudancas: EdicaoDeAgendamento = {};
        if (form.data !== original.data || form.hora !== original.hora) mudancas.inicioEm = `${form.data}T${form.hora}:00-03:00`;
        if (form.profissionalId && form.profissionalId !== original.profissionalId) mudancas.profissionalId = form.profissionalId;
        if (form.procedimentoId && form.procedimentoId !== original.procedimentoId) mudancas.procedimentoId = form.procedimentoId;
        if (form.salaId !== original.salaId) mudancas.salaId = form.salaId || null;
        if (form.status !== original.status) mudancas.status = form.status;
        if (form.observacoes !== original.observacoes) mudancas.observacoes = form.observacoes;

        try {
            const salvo = await dados().atualizarAgendamento(base.id, mudancas, registros);
            setHistorico((h) => [...registros, ...h]);
            setBase(salvo);
            setEditando(false);
            onSalvo();
            if (mudados.length > 0 && salvo.pacienteId) {
                setNotificar(mensagemDeAviso(salvo));
                aviso({
                    title: 'Alterações salvas',
                    description: `${mudados.length} ${mudados.length === 1 ? 'campo relevante' : 'campos relevantes'} · avise o paciente`,
                });
            } else {
                aviso({ title: 'Alterações salvas' });
            }
            return true;
        } catch (e) {
            aviso({ title: 'Não foi possível salvar', description: e instanceof Error ? e.message : undefined, tone: 'danger' });
            return false;
        } finally {
            setSalvando(false);
        }
    };

    /* Cancelar era Editar → Situação → Cancelado → Salvar. Aqui é um clique com
       confirmação, e o resto é o mesmo caminho da edição: histórico, lembretes que
       param, o evento que sai do Google Agenda e o aviso ao paciente oferecido. */
    const cancelar = async () => {
        const ok = await confirmar({
            titulo: `Cancelar o agendamento de ${base.paciente}?`,
            descricao: `${dataCurta(base.inicioEm)} às ${horaDe(base.inicioEm)}. Os lembretes param e o paciente não é avisado sozinho: depois você escolhe como avisar.`,
            confirmar: 'Cancelar agendamento',
        });
        if (!ok) return;
        setSalvando(true);
        const agora = new Date().toISOString();
        const registro: RegistroDeHistorico = {
            id: `${base.id}-status-${agora}`,
            titulo: 'Cancelou o agendamento',
            quandoEm: agora,
            autor: atual()?.nome ?? 'Usuário',
            icone: 'calendar-x',
            tom: 'danger',
            descricao: `${STATUS_OPCOES.find((s) => s.value === base.status)?.label ?? base.status} → Cancelado`,
        };
        try {
            const salvo = await dados().atualizarAgendamento(base.id, { status: 'cancelado' }, [registro]);
            setHistorico((h) => [registro, ...h]);
            setBase(salvo);
            setAba('info');
            onSalvo();
            if (salvo.pacienteId) setNotificar(mensagemDeAviso(salvo));
            aviso({ title: 'Agendamento cancelado', description: salvo.pacienteId ? 'Avise o paciente' : undefined, tone: 'warning' });
        } catch (e) {
            aviso({ title: 'Não foi possível cancelar', description: e instanceof Error ? e.message : undefined, tone: 'danger' });
        } finally {
            setSalvando(false);
        }
    };

    const alternarLembrete = async (l: Lembrete, ativo: boolean) => {
        setLembretes((atuais) => atuais.map((x) => (x.id === l.id ? { ...x, ativo } : x)));
        try {
            setLembretes(await dados().definirLembreteDoAgendamento(base.id, l.id, ativo));
        } catch (e) {
            setLembretes((atuais) => atuais.map((x) => (x.id === l.id ? { ...x, ativo: !ativo } : x)));
            aviso({ title: 'Não foi possível salvar o lembrete', description: e instanceof Error ? e.message : undefined, tone: 'danger' });
        }
    };

    const vinculo = base.vinculo;
    const ehLead = vinculo?.tipo === 'lead';

    return (
        <>
            <Modal
                open onClose={onClose} width={620} dirty={editando && sujo} onSave={salvar}
                title={base.status === 'bloqueio' ? base.paciente : `${base.paciente} · ${horaDe(base.inicioEm)}`}
                description={`${dataCurta(base.inicioEm)} · ${base.procedimento || 'Sem procedimento'}`}
                footer={
                    editando
                        ? (
                            <>
                                <Button variant="secondary" onClick={() => { setForm(original); setEditando(false); setErroDoForm(null); }}>Cancelar edição</Button>
                                <Button onClick={() => void salvar()} loading={salvando} disabled={!sujo}>Salvar alterações</Button>
                            </>
                        )
                        : (
                            <>
                                {CANCELAVEIS.includes(base.status) && (
                                    <>
                                        <Button variant="ghost" onClick={() => void cancelar()} disabled={salvando}>Cancelar agendamento</Button>
                                        <span style={{ marginLeft: 'auto' }} />
                                    </>
                                )}
                                <Button variant="secondary" onClick={onClose}>Fechar</Button>
                                <Button iconLeft={<Icon name="pencil" size={15} />} onClick={() => setEditando(true)}>Editar</Button>
                            </>
                        )
                }
            >
                <Tabs
                    ariaLabel="Seções do agendamento" activeId={aba} onChange={setAba}
                    tabs={[
                        { id: 'info', label: 'Informações' },
                        { id: 'lembretes', label: 'Lembretes', count: lembretes.filter((l) => l.ativo).length },
                        { id: 'historico', label: 'Histórico', count: historico.length },
                    ]}
                />

                <div style={{ paddingTop: 16 }}>
                    {aba === 'info' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                            {vinculo && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 11, padding: 12, background: 'var(--surface-secondary)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)' }}>
                                    <Avatar name={vinculo.nome} size="md" />
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <p style={{ margin: 0, fontSize: 13.5, fontWeight: 600, color: 'var(--text-primary)' }}>{vinculo.nome}</p>
                                        <p style={{ margin: '2px 0 0', fontSize: 12.5, color: 'var(--text-secondary)' }}>
                                            {ehLead ? `Lead · ${vinculo.oportunidade ?? ''}${vinculo.etapa ? ` · ${vinculo.etapa}` : ''}` : 'Paciente'}
                                        </p>
                                    </div>
                                    {/* Lead não tem prontuário: o botão leva à oportunidade,
                                        não a um cadastro de paciente que não existe. */}
                                    <Button
                                        variant="secondary" size="sm"
                                        onClick={() => (ehLead ? onAbrirLead(vinculo) : onAbrirPaciente(vinculo.id))}
                                    >
                                        {ehLead ? 'Ver oportunidade' : 'Abrir paciente'}
                                    </Button>
                                </div>
                            )}

                            {notificar && (
                                <NotificarPaciente
                                    contatoId={base.pacienteId} nome={base.paciente} mensagem={notificar}
                                    onFeito={() => setNotificar(null)} onDepois={() => setNotificar(null)}
                                />
                            )}

                            {editando ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                    {erroDoForm && <Alert tone="danger" dismissible={false}>{erroDoForm}</Alert>}
                                    {erroDasListas && (
                                        <Alert tone="warning" title="O cadastro não carregou" dismissible={false}>
                                            Profissional, procedimento e sala não podem ser trocados agora: {erroDasListas}
                                        </Alert>
                                    )}
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                                        <FormField label="Data" htmlFor={idData} required>
                                            <Input id={idData} type="date" value={form.data} onChange={(v) => alterar('data', v)} invalid={!form.data && !!erroDoForm} />
                                        </FormField>
                                        <FormField label="Horário" htmlFor={idHora} required>
                                            <Input id={idHora} type="time" value={form.hora} onChange={(v) => alterar('hora', v)} invalid={!form.hora && !!erroDoForm} />
                                        </FormField>
                                        <FormField label="Procedimento" htmlFor={idProcedimento}>
                                            <Select
                                                id={idProcedimento} value={form.procedimentoId} onChange={(v) => alterar('procedimentoId', v)}
                                                placeholder={!original.procedimentoId && base.procedimento ? `Fora do cadastro: ${base.procedimento}` : 'Selecione'}
                                                options={listas.procedimentos.map((p) => ({ value: p.id, label: p.nome }))}
                                            />
                                        </FormField>
                                        <FormField label="Profissional" htmlFor={idProfissional}>
                                            <Select
                                                id={idProfissional} value={form.profissionalId} onChange={(v) => alterar('profissionalId', v)}
                                                placeholder={!original.profissionalId && base.profissional ? `Fora do cadastro: ${base.profissional}` : 'Selecione'}
                                                options={listas.profissionais.map((p) => ({ value: p.id, label: p.nome }))}
                                            />
                                        </FormField>
                                        <FormField label="Sala" htmlFor={idSala}>
                                            <Select
                                                id={idSala} value={form.salaId} onChange={(v) => alterar('salaId', v)} placeholder="Sem sala"
                                                options={listas.salas.map((s) => ({ value: s.id, label: s.nome }))}
                                            />
                                        </FormField>
                                        <FormField label="Situação" htmlFor={idSituacao}>
                                            <Select id={idSituacao} value={form.status} onChange={(v) => alterar('status', v as StatusDoAgendamento)} options={STATUS_OPCOES} />
                                        </FormField>
                                        <FormField label="Observações" hint="Visível apenas para a equipe" style={{ gridColumn: '1 / -1' }}>
                                            <Textarea value={form.observacoes} onChange={(v) => alterar('observacoes', v)} rows={3} />
                                        </FormField>
                                    </div>
                                </div>
                            ) : (
                                <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '9px 16px', fontSize: 13 }}>
                                    <dt style={{ color: 'var(--text-tertiary)' }}>Situação</dt>
                                    <dd style={{ margin: 0 }}><Badge tone={STATUS_TOM[base.status]}>{STATUS_OPCOES.find((s) => s.value === base.status)?.label ?? 'Bloqueio'}</Badge></dd>
                                    <dt style={{ color: 'var(--text-tertiary)' }}>Quando</dt>
                                    <dd style={{ margin: 0, color: 'var(--text-primary)' }}>{dataCurta(base.inicioEm)} · {horaDe(base.inicioEm)} · {base.duracaoMin} min</dd>
                                    <dt style={{ color: 'var(--text-tertiary)' }}>Profissional</dt>
                                    <dd style={{ margin: 0, color: 'var(--text-primary)' }}>{base.profissional || '—'}</dd>
                                    <dt style={{ color: 'var(--text-tertiary)' }}>Sala</dt>
                                    <dd style={{ margin: 0, color: 'var(--text-primary)' }}>{base.sala ?? '—'}</dd>
                                    {base.unidade && (
                                        <>
                                            <dt style={{ color: 'var(--text-tertiary)' }}>Unidade</dt>
                                            <dd style={{ margin: 0, color: 'var(--text-primary)' }}>{base.unidade}</dd>
                                        </>
                                    )}
                                    {base.linkTeleconsulta && (
                                        <>
                                            <dt style={{ color: 'var(--text-tertiary)' }}>Sala de vídeo</dt>
                                            <dd style={{ margin: 0 }}>
                                                <a href={base.linkTeleconsulta} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--text-link)', fontWeight: 600 }}>
                                                    Abrir a sala da teleconsulta
                                                </a>
                                            </dd>
                                        </>
                                    )}
                                    <dt style={{ color: 'var(--text-tertiary)' }}>Observações</dt>
                                    <dd style={{ margin: 0, color: 'var(--text-primary)' }}>{base.observacoes || '—'}</dd>
                                </dl>
                            )}
                        </div>
                    )}

                    {aba === 'lembretes' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            {erroDeCarga && <Alert tone="danger" title="Os lembretes não carregaram" dismissible={false}>{erroDeCarga}</Alert>}
                            <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-secondary)' }}>
                                A régua é da clínica. Desligar um lembrete aqui vale só para este agendamento.
                            </p>
                            {lembretes.map((l) => (
                                <div key={l.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: 12, border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)' }}>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <p style={{ margin: 0, fontSize: 13.5, fontWeight: 600, color: 'var(--text-primary)' }}>{l.quando}</p>
                                        <p style={{ margin: '2px 0 0', fontSize: 12.5, color: 'var(--text-secondary)' }}>{l.descricao} · {CANAL_ROTULO[l.canal]}</p>
                                    </div>
                                    <Switch checked={l.ativo} ariaLabel={`Lembrete ${l.quando}`} onChange={(v) => void alternarLembrete(l, v)} />
                                </div>
                            ))}
                            <Divider spacing={4} />
                            <p style={{ margin: 0, fontSize: 12, color: 'var(--text-tertiary)' }}>
                                A régua da clínica fica em Configurações › Notificações.
                            </p>
                        </div>
                    )}

                    {aba === 'historico' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            {erroDeCarga && <Alert tone="danger" title="O histórico não carregou" dismissible={false}>{erroDeCarga}</Alert>}
                            <Timeline
                                events={historico.map((h) => ({
                                    id: h.id,
                                    title: h.titulo,
                                    time: dataEHoraPorExtenso(h.quandoEm),
                                    author: h.autor,
                                    icon: h.icone,
                                    tone: h.tom,
                                    description: h.descricao,
                                }))}
                            />
                        </div>
                    )}
                </div>
            </Modal>
            {confirmacao}
        </>
    );
}
